import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createCertificateReview,
  deleteCertificate,
  deleteCertificateAttachment,
  deleteCertificateReview,
  downloadCertificate,
  downloadCertificateAttachment,
  fetchCertificate,
  fetchCertificates,
  updateCertificate,
  updateCertificateReview,
  uploadCertificate,
  uploadCertificateAttachments,
} from '../../api/certificate';
import { useCompanyStore } from '../../store/companyStore';
import { notifyError, reportError } from '../../utils/notify';
import {
  Certificate,
  CertificateAttachment,
  CertificateReview,
  CertificateReviewManager,
  CERTIFICATE_REVIEW_TYPES,
  certificateDaysUntil,
  certificateRemainText,
  certificateValidityLabel,
  formatCertPeriod,
  formatCertScopes,
  formatFileSize,
} from '../../types/product';

import '../contract/ContractPanel.css';
import '../equipment/EquipTabPanel.css';
import '../equipment/InspectionCalibOverview.css';
import './CertificatePanel.css';

type PanelView =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'detail'; certificateId: number };

const LIST_VIEW: PanelView = { type: 'list' };

type ValidityFilter = 'ALL' | 'EXPIRED' | 'EXPIRING' | 'OK' | 'NONE';
type ValiditySort = 'status' | 'validTo';
type SortDir = 'asc' | 'desc';

const EMPTY_MANAGER: CertificateReviewManager = { managerName: '', contact: '' };

function validityRank(status?: string | null) {
  switch (status) {
    case 'EXPIRED':
      return 0;
    case 'EXPIRING':
      return 1;
    case 'NONE':
    case undefined:
    case null:
      return 2;
    default:
      return 3;
  }
}

function CertificatePanel() {
  const companyId = useCompanyStore((state) => state.companyId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<PanelView>(LIST_VIEW);
  const [rows, setRows] = useState<Certificate[]>([]);
  const [detail, setDetail] = useState<Certificate | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [validityFilter, setValidityFilter] = useState<ValidityFilter>('ALL');
  const [sortBy, setSortBy] = useState<ValiditySort>('status');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [scopes, setScopes] = useState<string[]>(['']);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [warnDaysText, setWarnDaysText] = useState('30');
  const [content, setContent] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const detailFileRef = useRef<HTMLInputElement>(null);

  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState<number | null>(null);
  const [reviewType, setReviewType] = useState<string>(CERTIFICATE_REVIEW_TYPES[0]);
  const [reviewDate, setReviewDate] = useState('');
  const [managers, setManagers] = useState<CertificateReviewManager[]>([{ ...EMPTY_MANAGER }]);
  const [reviewContent, setReviewContent] = useState('');

  const load = useCallback(async (keyword?: string) => {
    try {
      setLoading(true);
      const data = await fetchCertificates(keyword);
      setRows(data);
      setError('');
    } catch {
      reportError(setError, '인증서 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (certificateId: number) => {
    try {
      setDetailLoading(true);
      const data = await fetchCertificate(certificateId);
      setDetail(data);
      setTitle(data.title);
      setScopes(data.scopes && data.scopes.length > 0 ? data.scopes : ['']);
      setValidFrom(data.validFrom?.slice(0, 10) ?? '');
      setValidTo(data.validTo?.slice(0, 10) ?? '');
      setWarnDaysText(String(data.warnDays ?? 30));
      setContent(data.content ?? '');
      setError('');
    } catch {
      reportError(setError, '인증서를 불러오지 못했습니다.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    setView(LIST_VIEW);
    setSearchKeyword('');
    void load();
  }, [companyId, load, setView]);

  useEffect(() => {
    if (view.type !== 'list') return;
    const timer = window.setTimeout(() => {
      void load(searchKeyword);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, load, view.type]);

  useEffect(() => {
    if (view.type === 'detail') {
      void loadDetail(view.certificateId);
    }
  }, [view, loadDetail]);

  const cleanedScopes = () => scopes.map((s) => s.trim()).filter(Boolean);

  const resetCreateForm = () => {
    setTitle('');
    setScopes(['']);
    setValidFrom('');
    setValidTo('');
    setWarnDaysText('30');
    setContent('');
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetReviewForm = () => {
    setEditingReviewId(null);
    setReviewType(CERTIFICATE_REVIEW_TYPES[0]);
    setReviewDate('');
    setManagers([{ ...EMPTY_MANAGER }]);
    setReviewContent('');
    setShowReviewForm(false);
  };

  const parseWarnDays = () => {
    const n = Number(warnDaysText.trim());
    if (!Number.isFinite(n) || n < 0) return 30;
    return Math.floor(n);
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (pendingFiles.length === 0) {
      notifyError('인증서 파일을 한 개 이상 선택해 주세요.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const created = await uploadCertificate(pendingFiles, {
        title: title.trim() || undefined,
        scopes: cleanedScopes(),
        validFrom: validFrom || undefined,
        validTo: validTo || undefined,
        warnDays: parseWarnDays(),
        content: content.trim() || undefined,
      });
      resetCreateForm();
      await load(searchKeyword);
      setView({ type: 'detail', certificateId: created.certificateId });
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '저장에 실패했습니다.';
      notifyError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMeta = async (event: FormEvent) => {
    event.preventDefault();
    if (view.type !== 'detail') return;
    try {
      setSaving(true);
      setError('');
      await updateCertificate(view.certificateId, {
        title: title.trim(),
        scopes: cleanedScopes(),
        validFrom: validFrom || null,
        validTo: validTo || null,
        warnDays: parseWarnDays(),
        content: content.trim(),
      });
      await loadDetail(view.certificateId);
      await load(searchKeyword);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '수정에 실패했습니다.';
      notifyError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: Certificate) => {
    if (
      !window.confirm(
        `'${row.title}' 인증서를 삭제할까요?\n첨부 파일·심사이력·제품 연결도 함께 삭제/해제됩니다.`
      )
    ) {
      return;
    }
    try {
      await deleteCertificate(row.certificateId);
      setView(LIST_VIEW);
      await load(searchKeyword);
    } catch {
      notifyError('삭제에 실패했습니다.');
    }
  };

  const handleUploadAttachments = async (files: FileList | null) => {
    if (view.type !== 'detail' || !detail || !files?.length) return;
    const selected = Array.from(files);
    if (detailFileRef.current) detailFileRef.current.value = '';
    try {
      setSaving(true);
      setError('');
      await uploadCertificateAttachments(detail.certificateId, selected);
      await loadDetail(detail.certificateId);
      await load(searchKeyword);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '첨부파일 업로드에 실패했습니다.';
      notifyError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (attachment: CertificateAttachment) => {
    if (view.type !== 'detail' || !detail || attachment.attachmentId == null) return;
    if (!window.confirm(`「${attachment.fileName}」 첨부를 삭제할까요?`)) return;
    try {
      setSaving(true);
      await deleteCertificateAttachment(detail.certificateId, attachment.attachmentId);
      await loadDetail(detail.certificateId);
      await load(searchKeyword);
    } catch {
      notifyError('첨부파일 삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAttachment = async (attachment: CertificateAttachment) => {
    if (!detail) return;
    try {
      if (attachment.attachmentId != null) {
        await downloadCertificateAttachment(
          detail.certificateId,
          attachment.attachmentId,
          attachment.fileName
        );
      } else {
        await downloadCertificate(detail.certificateId, attachment.fileName || detail.fileName || 'file');
      }
    } catch {
      notifyError('첨부파일 다운로드에 실패했습니다.');
    }
  };

  const detailAttachments = useMemo(() => {
    if (!detail) return [] as CertificateAttachment[];
    if (detail.attachments && detail.attachments.length > 0) return detail.attachments;
    if (detail.fileName) {
      return [
        {
          attachmentId: null,
          fileName: detail.fileName,
          fileExt: detail.fileExt,
          fileSize: detail.fileSize,
        },
      ];
    }
    return [];
  }, [detail]);

  const openReviewCreate = () => {
    resetReviewForm();
    setShowReviewForm(true);
  };

  const openReviewEdit = (row: CertificateReview) => {
    setEditingReviewId(row.reviewId);
    setReviewType(row.reviewType);
    setReviewDate(row.reviewDate?.slice(0, 10) ?? '');
    setManagers(
      row.managers && row.managers.length > 0
        ? row.managers.map((m) => ({
            managerName: m.managerName ?? '',
            contact: m.contact ?? '',
          }))
        : [{ ...EMPTY_MANAGER }]
    );
    setReviewContent(row.content ?? '');
    setShowReviewForm(true);
  };

  const handleReviewSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (view.type !== 'detail') return;
    const cleanedManagers = managers
      .map((m) => ({
        managerName: m.managerName.trim(),
        contact: (m.contact ?? '').trim(),
      }))
      .filter((m) => m.managerName || m.contact);
    if (!reviewDate) {
      notifyError('심사일자를 입력해 주세요.');
      return;
    }
    if (cleanedManagers.length === 0 || cleanedManagers.some((m) => !m.managerName || !m.contact)) {
      notifyError('담당자 이름과 연락처를 모두 입력해 주세요.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        reviewType,
        reviewDate,
        content: reviewContent.trim() || undefined,
        managers: cleanedManagers,
      };
      if (editingReviewId == null) {
        await createCertificateReview(view.certificateId, payload);
      } else {
        await updateCertificateReview(view.certificateId, editingReviewId, payload);
      }
      resetReviewForm();
      await loadDetail(view.certificateId);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '심사이력 저장에 실패했습니다.';
      notifyError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleReviewDelete = async (row: CertificateReview) => {
    if (view.type !== 'detail') return;
    if (!window.confirm(`${row.reviewType} 이력을 삭제할까요?`)) return;
    try {
      await deleteCertificateReview(view.certificateId, row.reviewId);
      await loadDetail(view.certificateId);
    } catch {
      notifyError('심사이력 삭제에 실패했습니다.');
    }
  };

  const formatManagers = (list?: CertificateReviewManager[] | null) => {
    if (!list || list.length === 0) return '-';
    return list
      .map((m) => (m.contact ? `${m.managerName} (${m.contact})` : m.managerName))
      .join(', ');
  };

  const reviews = detail?.reviews ?? [];

  const validityCounts = useMemo(
    () => ({
      ALL: rows.length,
      EXPIRED: rows.filter((r) => r.validityStatus === 'EXPIRED').length,
      EXPIRING: rows.filter((r) => r.validityStatus === 'EXPIRING').length,
      OK: rows.filter((r) => r.validityStatus === 'OK').length,
      NONE: rows.filter((r) => !r.validityStatus || r.validityStatus === 'NONE').length,
    }),
    [rows]
  );

  const sortedRows = useMemo(() => {
    const filtered = rows.filter((row) =>
      validityFilter === 'ALL' ? true : (row.validityStatus || 'NONE') === validityFilter
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      if (sortBy === 'validTo') {
        const da = certificateDaysUntil(a.validTo);
        const db = certificateDaysUntil(b.validTo);
        if (da == null && db == null) return a.title.localeCompare(b.title);
        if (da == null) return 1;
        if (db == null) return -1;
        if (da !== db) return (da - db) * dir;
        return a.title.localeCompare(b.title);
      }
      const rank = validityRank(a.validityStatus) - validityRank(b.validityStatus);
      if (rank !== 0) return rank;
      const da = certificateDaysUntil(a.validTo);
      const db = certificateDaysUntil(b.validTo);
      if (da == null && db == null) return a.title.localeCompare(b.title);
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    });
  }, [rows, validityFilter, sortBy, sortDir]);

  const toggleValidToSort = () => {
    if (sortBy !== 'validTo') {
      setSortBy('validTo');
      setSortDir('asc');
      return;
    }
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const validToSortMark =
    sortBy === 'validTo' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const renderScopeTable = () => (
    <div className="cert-form__full">
      <div className="cert-form__section-head" style={{ marginBottom: 8 }}>
        <h4 style={{ margin: 0, fontSize: 12, color: '#667085' }}>범위 항목</h4>
        <button
          type="button"
          className="is-ghost"
          onClick={() => setScopes((prev) => [...prev, ''])}
        >
          행 추가
        </button>
      </div>
      <table className="cert-scope-table">
        <thead>
          <tr>
            <th style={{ width: 48 }}>No</th>
            <th>범위</th>
            <th style={{ width: 72 }} />
          </tr>
        </thead>
        <tbody>
          {scopes.map((scope, index) => (
            <tr key={`scope-${index}`}>
              <td>{index + 1}</td>
              <td>
                <input
                  value={scope}
                  onChange={(e) =>
                    setScopes((prev) =>
                      prev.map((item, i) => (i === index ? e.target.value : item))
                    )
                  }
                  placeholder="예: LED 조명 제품군"
                  maxLength={100}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="is-danger"
                  disabled={scopes.length <= 1}
                  onClick={() => setScopes((prev) => prev.filter((_, i) => i !== index))}
                >
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderManagerTable = () => (
    <div className="cert-form__full">
      <div className="cert-form__section-head" style={{ marginBottom: 8 }}>
        <h4 style={{ margin: 0, fontSize: 12, color: '#667085' }}>담당자</h4>
        <button
          type="button"
          className="is-ghost"
          onClick={() => setManagers((prev) => [...prev, { ...EMPTY_MANAGER }])}
        >
          행 추가
        </button>
      </div>
      <table className="cert-scope-table">
        <thead>
          <tr>
            <th style={{ width: 48 }}>No</th>
            <th>이름 *</th>
            <th>연락처 *</th>
            <th style={{ width: 72 }} />
          </tr>
        </thead>
        <tbody>
          {managers.map((manager, index) => (
            <tr key={`mgr-${index}`}>
              <td>{index + 1}</td>
              <td>
                <input
                  value={manager.managerName}
                  onChange={(e) =>
                    setManagers((prev) =>
                      prev.map((item, i) =>
                        i === index ? { ...item, managerName: e.target.value } : item
                      )
                    )
                  }
                  placeholder="예: 홍길동"
                  maxLength={100}
                />
              </td>
              <td>
                <input
                  value={manager.contact ?? ''}
                  onChange={(e) =>
                    setManagers((prev) =>
                      prev.map((item, i) =>
                        i === index ? { ...item, contact: e.target.value } : item
                      )
                    )
                  }
                  placeholder="예: 010-1234-5678"
                  maxLength={100}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="is-danger"
                  disabled={managers.length <= 1}
                  onClick={() => setManagers((prev) => prev.filter((_, i) => i !== index))}
                >
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <main
      className="contract-panel"
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <section
        className="contract-panel__main"
        style={{ width: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        <div className="equip-tab-panel" style={{ padding: 16 }}>
          {view.type === 'list' && (
            <div className="cert-panel">
              <div className="cert-panel__toolbar">
                <div>
                  <h3>회사 인증서</h3>
                  <p className="cert-panel__hint">
                    인증범위·유효기간을 등록하고, 공장/정기심사 이력을 누적 관리합니다.
                  </p>
                </div>
                <div className="cert-panel__actions">
                  <input
                    className="cert-search"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    placeholder="인증명 검색"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      resetCreateForm();
                      setView({ type: 'create' });
                    }}
                  >
                    추가
                  </button>
                </div>
              </div>

              {error && <p className="equip-tab-panel__error">{error}</p>}

              {loading ? (
                <p className="cert-panel__hint">불러오는 중...</p>
              ) : rows.length === 0 ? (
                <p className="cert-panel__hint">인증서를 추가해 주세요.</p>
              ) : (
                <div className="insp-calib-overview">
                  <div className="insp-calib-overview__summary">
                    {(
                      [
                        ['ALL', '전체'],
                        ['EXPIRED', '만료'],
                        ['EXPIRING', '만료 임박'],
                        ['OK', '유효'],
                        ['NONE', '미등록'],
                      ] as const
                    ).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        className={`insp-calib-overview__chip is-${key.toLowerCase()}${
                          validityFilter === key ? ' is-active' : ''
                        }`}
                        onClick={() => setValidityFilter(key)}
                      >
                        <strong>{validityCounts[key]}</strong>
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                  <p className="insp-calib-overview__hint">
                    기본은 상태(만료→임박→유효) 순입니다. <strong>유효기간</strong> 또는{' '}
                    <strong>잔여</strong> 헤더를 클릭하면 종료일 기준 오름/내림차순으로 정렬됩니다.
                    만료 임박은 인증서별 알림 일수(기본 30일) 이내입니다.
                  </p>
                  {sortedRows.length === 0 ? (
                    <p className="cert-panel__hint">해당 상태의 인증서가 없습니다.</p>
                  ) : (
                    <table className="equip-tab-panel__table insp-calib-overview__table">
                      <thead>
                        <tr>
                          <th style={{ width: '100px' }}>상태</th>
                          <th>인증명</th>
                          <th>인증범위</th>
                          <th style={{ width: '180px' }}>
                            <button
                              type="button"
                              className="equip-tab-panel__sort-th"
                              onClick={toggleValidToSort}
                              title="유효기간 종료일 기준 정렬"
                            >
                              유효기간{validToSortMark}
                            </button>
                          </th>
                          <th style={{ width: '100px' }}>
                            <button
                              type="button"
                              className="equip-tab-panel__sort-th"
                              onClick={toggleValidToSort}
                              title="잔여일 기준 정렬"
                            >
                              잔여{validToSortMark}
                            </button>
                          </th>
                          <th style={{ width: '120px' }} />
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((row) => {
                          const status = row.validityStatus || 'NONE';
                          const rowClass =
                            status === 'EXPIRED'
                              ? 'is-expired'
                              : status === 'EXPIRING'
                                ? 'is-expiring'
                                : undefined;
                          return (
                            <tr
                              key={row.certificateId}
                              className={rowClass}
                              onClick={() =>
                                setView({ type: 'detail', certificateId: row.certificateId })
                              }
                            >
                              <td>
                                <span
                                  className={`equip-calib-status is-${status.toLowerCase()}`}
                                >
                                  {certificateValidityLabel(status)}
                                </span>
                              </td>
                              <td>{row.title}</td>
                              <td>{formatCertScopes(row.scopes)}</td>
                              <td>{formatCertPeriod(row.validFrom, row.validTo)}</td>
                              <td>{certificateRemainText(row.validTo)}</td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className="equip-tab-panel__row-actions">
                                  <button
                                    type="button"
                                    className="is-ghost"
                                    onClick={() =>
                                      setView({
                                        type: 'detail',
                                        certificateId: row.certificateId,
                                      })
                                    }
                                  >
                                    상세
                                  </button>
                                  <button
                                    type="button"
                                    className="is-danger"
                                    onClick={() => void handleDelete(row)}
                                  >
                                    삭제
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}

          {view.type === 'create' && (
            <div className="cert-panel">
              <div className="cert-panel__toolbar">
                <div>
                  <h3>인증서 등록</h3>
                  <p className="cert-panel__hint">
                    인증명·유효기간·범위를 입력하고 관련 파일을 함께 첨부하세요.
                  </p>
                </div>
                <div className="cert-panel__actions">
                  <button type="button" className="is-ghost" onClick={() => setView(LIST_VIEW)}>
                    목록
                  </button>
                </div>
              </div>
              {error && <p className="equip-tab-panel__error">{error}</p>}
              <form className="cert-form" onSubmit={(e) => void handleCreate(e)}>
                <section className="cert-form__section">
                  <div className="cert-form__section-head">
                    <h4>기본 정보</h4>
                  </div>
                  <div className="cert-form__grid">
                    <label className="is-span-3">
                      인증명
                      <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={255}
                        placeholder="예: KS 인증"
                      />
                    </label>
                    <label>
                      유효기간 시작
                      <input
                        type="date"
                        value={validFrom}
                        onChange={(e) => setValidFrom(e.target.value)}
                      />
                    </label>
                    <label>
                      유효기간 종료
                      <input
                        type="date"
                        value={validTo}
                        onChange={(e) => setValidTo(e.target.value)}
                      />
                    </label>
                    <label>
                      만료 임박 알림 (일)
                      <input
                        type="number"
                        min={0}
                        value={warnDaysText}
                        onChange={(e) => setWarnDaysText(e.target.value)}
                        placeholder="30"
                      />
                    </label>
                    <label className="is-span-3">
                      비고
                      <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        maxLength={2000}
                        rows={3}
                        placeholder="인증 비고·특이사항을 입력하세요"
                      />
                    </label>
                  </div>
                </section>

                <section className="cert-form__section">
                  <div className="cert-form__section-head">
                    <h4>인증범위</h4>
                  </div>
                  {renderScopeTable()}
                </section>

                <section className="cert-form__section">
                  <div className="cert-form__section-head">
                    <h4>첨부파일 *</h4>
                    <button
                      type="button"
                      className="cert-attach-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      파일 선택
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      hidden
                      onChange={(e) => {
                        const next = Array.from(e.target.files ?? []);
                        setPendingFiles((prev) => [...prev, ...next].slice(0, 20));
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <p className="cert-panel__hint">여러 파일을 선택할 수 있습니다. (최대 20개)</p>
                  {pendingFiles.length > 0 ? (
                    <ul className="cert-pending">
                      {pendingFiles.map((file, idx) => (
                        <li key={`${file.name}-${idx}`}>
                          <span>
                            {file.name}
                            <span className="cert-files__meta">({formatFileSize(file.size)})</span>
                          </span>
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() =>
                              setPendingFiles((prev) => prev.filter((_, i) => i !== idx))
                            }
                          >
                            제거
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="cert-files__empty">선택된 파일이 없습니다.</p>
                  )}
                </section>

                <div className="cert-form__footer">
                  <button type="button" className="is-ghost" onClick={() => setView(LIST_VIEW)}>
                    취소
                  </button>
                  <button type="submit" disabled={saving}>
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {view.type === 'detail' && (
            <div className="cert-panel">
              <div className="cert-panel__toolbar">
                <div>
                  <h3>
                    {detail?.title || '인증서 상세'}
                    {detail?.validityStatus ? (
                      <span
                        className={`equip-calib-status is-${(
                          detail.validityStatus || 'NONE'
                        ).toLowerCase()}`}
                        style={{ marginLeft: 8, verticalAlign: 'middle' }}
                      >
                        {certificateValidityLabel(detail.validityStatus)}
                        {detail.validTo
                          ? ` · ${certificateRemainText(detail.validTo)}`
                          : ''}
                      </span>
                    ) : null}
                  </h3>
                  <p className="cert-panel__hint">
                    기본 정보를 수정하고, 공장/정기심사 이력을 계속 추가할 수 있습니다.
                  </p>
                </div>
                <div className="cert-panel__actions">
                  {detail ? (
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => void handleDelete(detail)}
                    >
                      인증서 삭제
                    </button>
                  ) : null}
                  <button type="button" className="is-ghost" onClick={() => setView(LIST_VIEW)}>
                    목록
                  </button>
                </div>
              </div>

              {error && <p className="equip-tab-panel__error">{error}</p>}
              {detailLoading ? (
                <p className="cert-panel__hint">불러오는 중...</p>
              ) : (
                <>
                  <form className="cert-form" onSubmit={(e) => void handleUpdateMeta(e)}>
                    <section className="cert-form__section">
                      <div className="cert-form__section-head">
                        <h4>기본 정보</h4>
                      </div>
                      <div className="cert-form__grid">
                        <label className="is-span-3">
                          인증명
                          <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            maxLength={255}
                            required
                          />
                        </label>
                        <label>
                          유효기간 시작
                          <input
                            type="date"
                            value={validFrom}
                            onChange={(e) => setValidFrom(e.target.value)}
                          />
                        </label>
                        <label>
                          유효기간 종료
                          <input
                            type="date"
                            value={validTo}
                            onChange={(e) => setValidTo(e.target.value)}
                          />
                        </label>
                        <label>
                          만료 임박 알림 (일)
                          <input
                            type="number"
                            min={0}
                            value={warnDaysText}
                            onChange={(e) => setWarnDaysText(e.target.value)}
                            placeholder="30"
                          />
                        </label>
                        <label className="is-span-3">
                          비고
                          <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            maxLength={2000}
                            rows={3}
                            placeholder="인증 비고·특이사항을 입력하세요"
                          />
                        </label>
                      </div>
                    </section>

                    <section className="cert-form__section">
                      <div className="cert-form__section-head">
                        <h4>인증범위</h4>
                      </div>
                      {renderScopeTable()}
                    </section>

                    <section className="cert-form__section">
                      <div className="cert-form__section-head">
                        <h4>첨부파일</h4>
                        <button
                          type="button"
                          className="cert-attach-btn"
                          disabled={saving}
                          onClick={() => detailFileRef.current?.click()}
                        >
                          파일 추가
                        </button>
                        <input
                          ref={detailFileRef}
                          type="file"
                          multiple
                          hidden
                          onChange={(e) => void handleUploadAttachments(e.target.files)}
                        />
                      </div>
                      {detailAttachments.length === 0 ? (
                        <p className="cert-files__empty">등록된 첨부파일이 없습니다.</p>
                      ) : (
                        <table className="cert-files">
                          <thead>
                            <tr>
                              <th>파일명</th>
                              <th style={{ width: 100 }}>크기</th>
                              <th style={{ width: 140 }} />
                            </tr>
                          </thead>
                          <tbody>
                            {detailAttachments.map((file, idx) => (
                              <tr key={file.attachmentId ?? `legacy-${idx}`}>
                                <td className="cert-files__name">{file.fileName}</td>
                                <td>
                                  {file.fileSize != null ? formatFileSize(file.fileSize) : '-'}
                                </td>
                                <td>
                                  <div className="cert-files__row-actions">
                                    <button
                                      type="button"
                                      className="is-ghost"
                                      onClick={() => void handleDownloadAttachment(file)}
                                    >
                                      다운로드
                                    </button>
                                    {file.attachmentId != null ? (
                                      <button
                                        type="button"
                                        className="is-danger"
                                        disabled={saving}
                                        onClick={() => void handleDeleteAttachment(file)}
                                      >
                                        삭제
                                      </button>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </section>

                    <div className="cert-form__footer">
                      <button type="submit" disabled={saving}>
                        {saving ? '저장 중...' : '정보 저장'}
                      </button>
                    </div>
                  </form>

                  <section className="cert-form__section">
                    <div className="cert-form__section-head">
                      <div>
                        <h4>심사이력</h4>
                        <p className="cert-panel__hint" style={{ marginTop: 4 }}>
                          공장심사·정기심사 등 이력을 담당자(복수)·연락처와 함께 기록합니다.
                        </p>
                      </div>
                      <button type="button" className="cert-attach-btn" onClick={openReviewCreate}>
                        이력 추가
                      </button>
                    </div>

                    {showReviewForm && (
                      <form
                        className="cert-form"
                        style={{ marginBottom: 12 }}
                        onSubmit={(e) => void handleReviewSubmit(e)}
                      >
                        <div className="cert-form__grid">
                          <label>
                            심사구분 *
                            <select
                              value={reviewType}
                              onChange={(e) => setReviewType(e.target.value)}
                            >
                              {CERTIFICATE_REVIEW_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            심사일자 *
                            <input
                              type="date"
                              value={reviewDate}
                              onChange={(e) => setReviewDate(e.target.value)}
                              required
                            />
                          </label>
                          <label className="is-span-3">
                            내용
                            <textarea
                              value={reviewContent}
                              onChange={(e) => setReviewContent(e.target.value)}
                              maxLength={2000}
                              rows={3}
                              placeholder="심사 결과, 지적사항, 후속조치 등"
                            />
                          </label>
                        </div>
                        {renderManagerTable()}
                        <div className="cert-form__footer">
                          <button type="button" className="is-ghost" onClick={resetReviewForm}>
                            취소
                          </button>
                          <button type="submit" disabled={saving}>
                            {saving ? '저장 중...' : editingReviewId == null ? '등록' : '수정 저장'}
                          </button>
                        </div>
                      </form>
                    )}

                    {reviews.length === 0 ? (
                      <p className="cert-files__empty">등록된 심사이력이 없습니다.</p>
                    ) : (
                      <table className="cert-files">
                        <thead>
                          <tr>
                            <th style={{ width: 48 }}>No</th>
                            <th style={{ width: 100 }}>구분</th>
                            <th style={{ width: 110 }}>심사일자</th>
                            <th style={{ width: 200 }}>담당자</th>
                            <th>내용</th>
                            <th style={{ width: 120 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {reviews.map((row, index) => (
                            <tr key={row.reviewId}>
                              <td>{index + 1}</td>
                              <td>{row.reviewType}</td>
                              <td>{row.reviewDate?.slice(0, 10) || '-'}</td>
                              <td>{formatManagers(row.managers)}</td>
                              <td style={{ whiteSpace: 'pre-wrap' }}>{row.content || '-'}</td>
                              <td>
                                <div className="cert-files__row-actions">
                                  <button
                                    type="button"
                                    className="is-ghost"
                                    onClick={() => openReviewEdit(row)}
                                  >
                                    수정
                                  </button>
                                  <button
                                    type="button"
                                    className="is-danger"
                                    onClick={() => void handleReviewDelete(row)}
                                  >
                                    삭제
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </section>
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default CertificatePanel;
